import { useNavigate } from "react-router-dom";
import { ArrowLeft, TrendingUp, TrendingDown, Building2, Award } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const branchData = [
  { branch: "Nairobi CBD", disbursed: 4200000, collected: 3900000, clients: 312, par: 2.1, score: 95 },
  { branch: "Westlands", disbursed: 3100000, collected: 2700000, clients: 198, par: 5.1, score: 78 },
  { branch: "Mombasa", disbursed: 2800000, collected: 2600000, clients: 187, par: 2.8, score: 88 },
  { branch: "Kisumu", disbursed: 1900000, collected: 1500000, clients: 145, par: 7.8, score: 58 },
  { branch: "Nakuru", disbursed: 3600000, collected: 3400000, clients: 210, par: 1.5, score: 92 },
  { branch: "Eldoret", disbursed: 2200000, collected: 1900000, clients: 172, par: 4.3, score: 72 },
].sort((a, b) => b.score - a.score);

const bdoData = [
  { name: "James Mwangi", branch: "Nairobi CBD", clients: 124, collected: 3800000, par: 2.1, score: 96 },
  { name: "Peter Kamau", branch: "Nakuru", clients: 110, collected: 3200000, par: 1.5, score: 94 },
  { name: "Ahmed Hassan", branch: "Mombasa", clients: 87, collected: 2500000, par: 2.8, score: 88 },
  { name: "Fatima Ali", branch: "Eldoret", clients: 72, collected: 1800000, par: 4.3, score: 71 },
  { name: "Sarah Otieno", branch: "Westlands", clients: 98, collected: 2400000, par: 5.1, score: 68 },
  { name: "Grace Wanjiku", branch: "Kisumu", clients: 65, collected: 1300000, par: 7.8, score: 52 },
].sort((a, b) => b.score - a.score);

const fmt = (n: number) => `KES ${(n / 1000000).toFixed(1)}M`;

const rankBadge = (idx: number, total: number) => {
  if (idx === 0) return <Badge className="bg-emerald-500/15 text-emerald-700 border-emerald-200 dark:text-emerald-400 dark:border-emerald-800">Best</Badge>;
  if (idx === total - 1) return <Badge variant="destructive">Worst</Badge>;
  return <Badge variant="secondary">#{idx + 1}</Badge>;
};

export default function PerformanceReportPage() {
  const navigate = useNavigate();

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/reports")} className="p-2 rounded-lg hover:bg-accent transition-colors">
          <ArrowLeft className="h-5 w-5 text-foreground" />
        </button>
        <BarChart className="h-6 w-6 text-primary" />
        <h1 className="text-2xl font-bold text-foreground">Performance Report</h1>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { icon: Building2, label: "Best Branch", value: branchData[0].branch, sub: `Score: ${branchData[0].score}` },
          { icon: TrendingDown, label: "Worst Branch", value: branchData[branchData.length - 1].branch, sub: `Score: ${branchData[branchData.length - 1].score}` },
          { icon: Award, label: "Best BDO", value: bdoData[0].name, sub: `Score: ${bdoData[0].score}` },
          { icon: TrendingDown, label: "Worst BDO", value: bdoData[bdoData.length - 1].name, sub: `Score: ${bdoData[bdoData.length - 1].score}` },
        ].map(s => (
          <Card key={s.label}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <s.icon className="h-4 w-4 text-muted-foreground" />
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </div>
              <p className="text-lg font-bold">{s.value}</p>
              <p className="text-xs text-muted-foreground">{s.sub}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="branches" className="space-y-4">
        <TabsList>
          <TabsTrigger value="branches">Branch Performance</TabsTrigger>
          <TabsTrigger value="bdos">BDO Performance</TabsTrigger>
        </TabsList>

        <TabsContent value="branches" className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Branch Score Comparison</CardTitle></CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={branchData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis dataKey="branch" tick={{ fontSize: 12 }} className="fill-muted-foreground" />
                    <YAxis className="fill-muted-foreground" />
                    <Tooltip />
                    <Bar dataKey="score" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Branch Rankings</CardTitle></CardHeader>
            <CardContent>
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Rank</TableHead>
                      <TableHead>Branch</TableHead>
                      <TableHead>Clients</TableHead>
                      <TableHead>Disbursed</TableHead>
                      <TableHead>Collected</TableHead>
                      <TableHead>PAR%</TableHead>
                      <TableHead>Score</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {branchData.map((r, i) => (
                      <TableRow key={r.branch}>
                        <TableCell>{rankBadge(i, branchData.length)}</TableCell>
                        <TableCell className="font-medium">{r.branch}</TableCell>
                        <TableCell>{r.clients}</TableCell>
                        <TableCell>{fmt(r.disbursed)}</TableCell>
                        <TableCell>{fmt(r.collected)}</TableCell>
                        <TableCell>{r.par}%</TableCell>
                        <TableCell className="font-bold">{r.score}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="md:hidden space-y-3">
                {branchData.map((r, i) => (
                  <Card key={r.branch}>
                    <CardContent className="p-4 space-y-1">
                      <div className="flex justify-between items-center">
                        <span className="font-medium">{r.branch}</span>
                        {rankBadge(i, branchData.length)}
                      </div>
                      <p className="text-sm">Score: <strong>{r.score}</strong> · PAR: {r.par}%</p>
                      <p className="text-xs text-muted-foreground">{r.clients} clients · Disbursed: {fmt(r.disbursed)}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="bdos" className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base">BDO Score Comparison</CardTitle></CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={bdoData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} className="fill-muted-foreground" />
                    <YAxis className="fill-muted-foreground" />
                    <Tooltip />
                    <Bar dataKey="score" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">BDO Rankings</CardTitle></CardHeader>
            <CardContent>
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Rank</TableHead>
                      <TableHead>BDO Name</TableHead>
                      <TableHead>Branch</TableHead>
                      <TableHead>Clients</TableHead>
                      <TableHead>Collected</TableHead>
                      <TableHead>PAR%</TableHead>
                      <TableHead>Score</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bdoData.map((r, i) => (
                      <TableRow key={r.name}>
                        <TableCell>{rankBadge(i, bdoData.length)}</TableCell>
                        <TableCell className="font-medium">{r.name}</TableCell>
                        <TableCell>{r.branch}</TableCell>
                        <TableCell>{r.clients}</TableCell>
                        <TableCell>{fmt(r.collected)}</TableCell>
                        <TableCell>{r.par}%</TableCell>
                        <TableCell className="font-bold">{r.score}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="md:hidden space-y-3">
                {bdoData.map((r, i) => (
                  <Card key={r.name}>
                    <CardContent className="p-4 space-y-1">
                      <div className="flex justify-between items-center">
                        <span className="font-medium">{r.name}</span>
                        {rankBadge(i, bdoData.length)}
                      </div>
                      <p className="text-sm">Score: <strong>{r.score}</strong> · PAR: {r.par}%</p>
                      <p className="text-xs text-muted-foreground">{r.branch} · {r.clients} clients · {fmt(r.collected)}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
